import { useMutation, useReactiveVar } from '@apollo/client'
import RoundButton from '@deltek/specification-client/core/Buttons/RoundButton'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import {
    Backdrop,
    CircularProgress,
    Theme,
    makeStyles,
} from '@material-ui/core'
import { DeleteProductTypeMutation } from 'api/graphQL/mutations/projectElement.mutations'
import { workspaceContents } from 'api/graphQL/reactiveVars/workspaceContent'
import { ProjectPageContext } from 'pages/ProjectsPage/ProjectPage/components/ProjectPageContext'
import RemoveProductTypeWarning from 'pages/ProjectsPage/ProjectsHomePage/components/RemoveProductTypeWarning'
import * as React from 'react'

export interface DeleteProductTypeButtonProps {
    updateElementList?: (
        elementList: ProjectElementNode[],
        parentElementNodes?: ProjectElementNode[],
        deletedElementNodes?: ProjectElementNode[]
    ) => void
    allElementsList?: ProjectElementNode[]
    isDisabled: boolean
}

export interface DeleteProductTypeInput {
    baseElementId: string
    documentTemplateViewId: string
    projectElementNodeId: string
    projectProductFamilyId: string
}

const useStyles = makeStyles((theme: Theme) => ({
    backdrop: {
        zIndex: theme.zIndex?.drawer + 1,
        color: theme.palette?.common.white,
    },
}))

const DeleteProductTypeButton: React.FunctionComponent<DeleteProductTypeButtonProps> = ({
    updateElementList,
    allElementsList,
    isDisabled,
}) => {
    const classes = useStyles()

    const [isOpenConfirmation, setIsOpenConfirmation] = React.useState<boolean>(
        false
    )

    const {
        selectedElementIdentifier,
        setSelectedElementIdentifier,
    } = React.useContext(ProjectPageContext)

    const { dtv, projectProductFamilyId } = useReactiveVar(workspaceContents)

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const baseElementId = React.useMemo(
        () => selectedElementIdentifier?.baseElementId,
        [selectedElementIdentifier]
    )

    const documentTemplateViewId = React.useMemo(() => dtv?.id, [dtv])

    const projectElementNodeId = React.useMemo(
        () => selectedElementIdentifier?.id,
        [selectedElementIdentifier]
    )

    const [deleteProductType, { loading: isDeleteInProgress }] = useMutation<{
        input: Partial<DeleteProductTypeInput>
    }>(DeleteProductTypeMutation, {
        onCompleted: (data: any) => {
            const deletedIds = [
                data?.deleteProductType?.baseElementId,
                ...data?.deleteProductType?.nestedBaseElementIds,
            ]

            const index = selectedElementIdentifier?.treePath.lastIndexOf('||')
            const key = selectedElementIdentifier?.treePath.slice(0, index)
            const currentFamilyItems = allElementsList?.filter((element) =>
                element?.treePath.includes(key as string)
            )

            let lastIndex = 0
            deletedIds.forEach((id, i) => {
                const selectedDataIndex =
                    i === 0
                        ? currentFamilyItems?.findIndex(
                              (selectedData) =>
                                  selectedData?.treePath ===
                                  selectedElementIdentifier?.treePath
                          )
                        : currentFamilyItems?.findIndex(
                              (selectedData) =>
                                  selectedData?.baseElementId === id
                          )

                lastIndex =
                    selectedDataIndex && selectedDataIndex > lastIndex
                        ? selectedDataIndex
                        : lastIndex
            })

            let selectedNode =
                currentFamilyItems && currentFamilyItems[lastIndex + 1]
                    ? currentFamilyItems[lastIndex + 1]
                    : currentFamilyItems?.[
                          currentFamilyItems?.length - (deletedIds.length + 1)
                      ]

            const selectedData = currentFamilyItems?.filter(
                (data) =>
                    deletedIds.includes(data?.baseElementId) ||
                    data.treePath === selectedElementIdentifier?.treePath
            )

            updateElementList!([], [], selectedData)
            setSelectedElementIdentifier({
                treePath: selectedNode?.treePath as string,
                baseElementId: selectedNode?.baseElementId as string,
            })
            showGlobalSnackbar({
                variables: {
                    message: `Product Type deleted successfully`,
                    variant: 'success',
                    id: 'WorkspacePage_DeleteProductTypeSuccess',
                },
            })
        },
        onError: (err) => {
            showGlobalSnackbar({
                variables: {
                    message: `There was a problem in deleting the product type.`,
                    variant: 'error',
                    id: 'WorkspacePage_DeleteProductTypeError',
                },
            })
        },
    })

    const toggleRemoveWarning = React.useCallback(() => {
        setIsOpenConfirmation(!isOpenConfirmation)
    }, [isOpenConfirmation])

    const openDeleteConfirmation = React.useCallback(() => {
        toggleRemoveWarning()
    }, [toggleRemoveWarning])

    const handleRemoveProjectClick = React.useCallback(() => {
        toggleRemoveWarning()
        deleteProductType({
            variables: {
                input: {
                    baseElementId,
                    documentTemplateViewId,
                    projectProductFamilyId,
                    projectElementNodeId,
                },
            },
        })
    }, [
        baseElementId,
        documentTemplateViewId,
        projectProductFamilyId,
        projectElementNodeId,
        toggleRemoveWarning,
    ])

    const isDeleteProductTypeDisabled = isDisabled || isOpenConfirmation

    return (
        <>
            <RoundButton
                size="small"
                hasShadow={false}
                color="#fff"
                borderColor={
                    isDeleteProductTypeDisabled ? '#0000001f' : '#EB5757'
                }
                backgroundColor="#EB5757"
                hoverColor="#E62325"
                data-testid="deleteProductTypeBtn"
                onClick={openDeleteConfirmation}
                disabled={isDeleteProductTypeDisabled}
            >
                Delete Product Type
            </RoundButton>
            <RemoveProductTypeWarning
                isDisplayed={isOpenConfirmation}
                onCancel={toggleRemoveWarning}
                onRemoveProject={handleRemoveProjectClick}
            />
            <Backdrop className={classes.backdrop} open={isDeleteInProgress}>
                <CircularProgress color="inherit" data-testid="addSpinner" />
            </Backdrop>
        </>
    )
}

export default DeleteProductTypeButton
